import { supabase } from '@/integrations/supabase/client';
import type {
  ProjectMilestone,
  ProjectMilestoneInsert,
  ProjectMilestoneUpdate,
} from '@/types/project.types';
import { projectService } from './projectService';

export const milestoneService = {
  // Fetch all milestones for a project
  async fetchProjectMilestones(projectId: string): Promise<ProjectMilestone[]> {
    const { data, error } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('project_id', projectId)
      .order('due_date', { ascending: true });

    if (error) throw error;
    return data as ProjectMilestone[];
  },

  // Fetch a single milestone
  async fetchMilestoneById(id: string): Promise<ProjectMilestone | null> {
    const { data, error } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ProjectMilestone;
  },

  // Create a new milestone
  async createMilestone(milestone: ProjectMilestoneInsert): Promise<ProjectMilestone> {
    const { data, error } = await supabase
      .from('project_milestones')
      .insert(milestone)
      .select()
      .single();

    if (error) throw error;

    // Log activity
    await projectService.logActivity({
      project_id: milestone.project_id,
      activity_type: 'milestone_created',
      title: 'Milestone Created',
      description: `Milestone "${data.milestone_name}" was created`,
      related_milestone_id: data.id,
    });

    return data as ProjectMilestone;
  },

  // Update a milestone
  async updateMilestone(id: string, update: ProjectMilestoneUpdate): Promise<ProjectMilestone> {
    // Get current milestone for activity logging
    const { data: currentMilestone } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('id', id)
      .single();

    const { data, error } = await supabase
      .from('project_milestones')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Log specific activities based on what changed
    if (currentMilestone && data) {
      let activityTitle = 'Milestone Updated';
      let activityDescription = `Milestone "${data.milestone_name}" was updated`;

      if (update.status && update.status !== currentMilestone.status) {
        if (update.status === 'completed') {
          activityTitle = 'Milestone Completed';
          activityDescription = `Milestone "${data.milestone_name}" was marked as completed`;
        } else if (update.status === 'cancelled') {
          activityTitle = 'Milestone Cancelled';
          activityDescription = `Milestone "${data.milestone_name}" was cancelled`;
        }
      }

      if (update.invoice_sent_date && !currentMilestone.invoice_sent_date) {
        activityTitle = 'Invoice Sent';
        activityDescription = `Invoice sent for milestone "${data.milestone_name}"`;
      }

      if (update.payment_received_date && !currentMilestone.payment_received_date) {
        activityTitle = 'Payment Received';
        activityDescription = `Payment received for milestone "${data.milestone_name}"`;
      }

      await projectService.logActivity({
        project_id: data.project_id,
        activity_type: 'milestone_updated',
        title: activityTitle,
        description: activityDescription,
        related_milestone_id: id,
      });
    }

    return data as ProjectMilestone;
  },

  // Delete a milestone
  async deleteMilestone(id: string): Promise<void> {
    // Get milestone info for activity logging
    const { data: milestone } = await supabase
      .from('project_milestones')
      .select('project_id, milestone_name')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('project_milestones')
      .delete()
      .eq('id', id);

    if (error) throw error;

    if (milestone) {
      await projectService.logActivity({
        project_id: milestone.project_id,
        activity_type: 'milestone_deleted',
        title: 'Milestone Deleted',
        description: `Milestone "${milestone.milestone_name}" was deleted`,
      });
    }
  },

  // Update milestone status based on date
  async checkOverdueMilestones(projectId: string): Promise<void> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data: overdueMilestones, error } = await supabase
      .from('project_milestones')
      .select('id')
      .eq('project_id', projectId)
      .in('status', ['pending', 'in_progress'])
      .lt('due_date', today);

    if (error) throw error;

    if (overdueMilestones && overdueMilestones.length > 0) {
      const milestoneIds = overdueMilestones.map(m => m.id);
      
      const { error: updateError } = await supabase
        .from('project_milestones')
        .update({ status: 'overdue' })
        .in('id', milestoneIds);

      if (updateError) throw updateError;
    }
  },

  // Calculate milestone statistics
  async getMilestoneStats(projectId: string): Promise<{
    total: number;
    completed: number;
    pending: number;
    overdue: number;
    total_value: number;
    completed_value: number;
    completion_rate: number;
  }> {
    const milestones = await this.fetchProjectMilestones(projectId);
    
    const stats = milestones.reduce(
      (acc, milestone) => {
        acc.total++;
        acc.total_value += milestone.amount || 0;
        
        if (milestone.status === 'completed') {
          acc.completed++;
          acc.completed_value += milestone.amount || 0;
        } else if (milestone.status === 'pending' || milestone.status === 'in_progress') {
          acc.pending++;
        } else if (milestone.status === 'overdue') {
          acc.overdue++;
        }
        
        return acc;
      },
      {
        total: 0,
        completed: 0,
        pending: 0,
        overdue: 0,
        total_value: 0,
        completed_value: 0,
        completion_rate: 0,
      }
    );
    
    stats.completion_rate = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
    
    return stats;
  },

  // Generate invoice for milestone
  async generateInvoice(milestoneId: string): Promise<{
    invoice_number: string;
    invoice_date: string;
    due_date: string;
  }> {
    const { data: milestone, error } = await supabase
      .from('project_milestones')
      .select(`
        *,
        project:project_id (
          name,
          project_code,
          organization:organization_id (
            name,
            address,
            email,
            phone
          )
        )
      `)
      .eq('id', milestoneId)
      .single();

    if (error) throw error;

    // Generate invoice number
    const invoiceDate = new Date();
    const year = invoiceDate.getFullYear();
    const month = String(invoiceDate.getMonth() + 1).padStart(2, '0');
    
    // Count invoices this month
    const { count } = await supabase
      .from('project_milestones')
      .select('*', { count: 'exact', head: true })
      .not('invoice_sent_date', 'is', null)
      .gte('invoice_sent_date', `${year}-${month}-01`);
    
    const invoiceNumber = `INV-${year}${month}-${String((count || 0) + 1).padStart(4, '0')}`;
    
    // Calculate due date (30 days from invoice date)
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(dueDate.getDate() + 30);
    
    // Update milestone with invoice sent date
    await this.updateMilestone(milestoneId, {
      invoice_sent_date: invoiceDate.toISOString().split('T')[0],
    });
    
    return {
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate.toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
    };
  },

  // Send payment reminder
  async sendPaymentReminder(milestoneId: string): Promise<void> {
    const { data: milestone } = await supabase
      .from('project_milestones')
      .select(`
        *,
        project:project_id (
          name,
          client_contact:client_contact_id (
            first_name,
            last_name,
            email
          )
        )
      `)
      .eq('id', milestoneId)
      .single();

    if (!milestone) throw new Error('Milestone not found');

    // Log reminder activity
    await projectService.logActivity({
      project_id: milestone.project_id,
      activity_type: 'payment_reminder_sent',
      title: 'Payment Reminder Sent',
      description: `Payment reminder sent for milestone "${milestone.milestone_name}"`,
      related_milestone_id: milestoneId,
      metadata: {
        contact_email: milestone.project?.client_contact?.email,
        invoice_date: milestone.invoice_sent_date,
        amount: milestone.amount,
      },
    });
  },
};